/**
 * Cashfree Checkout, wrapped in a promise.
 *
 * Same job as lib/razorpay.ts: turn a script-tag global driven by callbacks
 * into one `await`, so the calling component reads as an ordinary async flow.
 *
 * ── IT DIFFERS FROM RAZORPAY IN ONE WAY THAT MATTERS ────────────────────────
 *
 * Razorpay hands the browser a SIGNED payload on success, which the server
 * re-derives and verifies — so the browser can be told "your coins are here"
 * immediately. Cashfree does not. Its checkout resolves with a status and no
 * signature, and a status from a browser is a claim, not proof.
 *
 * So there is no /verify endpoint for Cashfree and this module never asserts a
 * payment succeeded. It reports that checkout CLOSED, and the caller re-reads
 * the wallet from the server. The webhook is the only thing that credits
 * coins, which is exactly the property that makes a forged success useless.
 *
 * The consequence is a short wait: the webhook usually lands within a second
 * or two, occasionally longer. PayDialog polls rather than pretending.
 */

const SCRIPT_SRC = 'https://sdk.cashfree.com/js/v3/cashfree.js';

/** `sandbox` unless explicitly told otherwise. Wrong-way-round is safer. */
export const CASHFREE_MODE: 'sandbox' | 'production' =
  import.meta.env.VITE_CASHFREE_ENV === 'production' ? 'production' : 'sandbox';

/**
 * The payment was attempted and refused — a declined card, a failed bank
 * authorisation. Distinct from a network problem because the outcome is known:
 * nothing was charged, and saying so plainly stops someone checking their
 * statement in a panic.
 */
export class CheckoutFailed extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CheckoutFailed';
  }
}

/** Thrown when the user closes the sheet without paying. */
export class CheckoutDismissed extends Error {
  constructor() {
    super('Payment cancelled.');
    this.name = 'CheckoutDismissed';
  }
}

interface CashfreeCheckoutResult {
  error?: { message?: string };
  redirect?: boolean;
  paymentDetails?: { paymentMessage?: string };
}

interface CashfreeInstance {
  checkout: (options: Record<string, unknown>) => Promise<CashfreeCheckoutResult>;
}

declare global {
  interface Window {
    Cashfree?: (config: { mode: string }) => CashfreeInstance;
  }
}

let loader: Promise<void> | null = null;

/**
 * Load the SDK once. The promise is cached rather than a boolean, so two
 * dialogs opened in the same tick share one load instead of injecting two
 * script tags.
 */
function loadCheckout(): Promise<void> {
  if (window.Cashfree) return Promise.resolve();
  if (loader) return loader;

  loader = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // Do not cache a failure — this is usually a dropped connection rather
      // than a permanent state, and the next attempt should retry.
      loader = null;
      reject(new Error('Could not load the payment window. Check your connection and try again.'));
    };
    document.head.appendChild(script);
  });

  return loader;
}

/**
 * Open the sheet and wait for it to close.
 *
 * Resolving means "checkout finished", NOT "payment succeeded" — see the note
 * at the top. A dismissal rejects with `CheckoutDismissed`, which the caller
 * should treat as nothing having happened.
 */
export async function openCheckout(paymentSessionId: string): Promise<void> {
  await loadCheckout();

  const Cashfree = window.Cashfree;
  if (!Cashfree) throw new Error('The payment window did not load. Try again.');

  const cashfree = Cashfree({ mode: CASHFREE_MODE });

  const result = await cashfree.checkout({
    paymentSessionId,
    // A modal rather than a redirect: leaving the SPA loses the dialog state
    // and lands the user back on a cold page.
    redirectTarget: '_modal',
  });

  if (result?.error) {
    /*
     * Cashfree reports a user-closed sheet as an error alongside genuine
     * failures. Treat the cancellation wording as a dismissal so the caller
     * can stay quiet about it; anything else is worth showing.
     */
    const message = result.error.message ?? '';
    if (/cancel|closed|dismiss/i.test(message)) throw new CheckoutDismissed();

    // Cashfree reached a verdict, so the outcome is known rather than
    // uncertain — which is what lets the dialog say "nothing was charged"
    // instead of hedging about money that may or may not have left.
    throw new CheckoutFailed(message || 'That payment did not go through.');
  }
}
